/**
 * Weighted field-coverage percentage for a single report type — same
 * filled/total contract as Coverage Matrix Status (report fields +
 * related site data in numerator; report schema fields in denominator).
 */
import { DOC_CHECKLISTS } from '../../config/documentChecklists';
import type { ReportTypeId } from '../../config/reportTypes';
import type { ExtractedClaim } from '../../types/siteContext';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectIntent } from '../../types/siteRag';
import { resolveReportFields } from './resolveReportFields';
import { filterRequirementsByIntent } from './requirementIntentRelevance';

export interface ReportTypeFieldCoverage {
  filled: number;
  total: number;
  pct: number;
}

export function computeReportTypeFieldCoverage(args: {
  reportType: ReportTypeId;
  sed?: SiteEvidenceDocument | null;
  authoredClaims?: readonly ExtractedClaim[];
  /**
   * Project intent — scopes the denominator (and numerator consistently) to
   * the requirements the intent actually needs. When omitted/empty the full
   * checklist is used (no regression). See `requirementIntentRelevance`.
   */
  projectIntent?: ProjectIntent | null;
}): ReportTypeFieldCoverage | null {
  const { reportType, sed, authoredClaims = [], projectIntent } = args;
  // Intent-scope the checklist BEFORE counting so both the numerator (filled)
  // and the denominator (total) only ever see intent-relevant requirements.
  const checklist = filterRequirementsByIntent(
    DOC_CHECKLISTS[reportType] ?? [],
    projectIntent,
  );
  let filled = 0;
  let total = 0;

  for (const item of checklist) {
    const structured = (item.authoredSchema ?? []).filter(f => f.key !== 'notes');
    if (structured.length === 0) continue;

    const claim = authoredClaims.find(c => c.requirementId === item.id);
    const resolved = resolveReportFields({
      requirement: item,
      sed: sed ?? undefined,
      authoredClaim: claim,
    });
    if (resolved.statusTotal <= 0) continue;
    filled += resolved.statusFilled;
    total += resolved.statusTotal;
  }

  if (total <= 0) return null;
  const pct = Math.round((filled / total) * 100);
  return { filled, total, pct };
}
